import React, { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { FaArrowLeft, FaArrowRight, FaPlus, FaMinus } from "react-icons/fa";
import { MdElectricBolt } from "react-icons/md";
import "swiper/css";
import "swiper/css/navigation";
import axios from "axios";
import { Link } from "react-router-dom";
import { addToCart, getCartCount } from "../../../utils/cartUtils";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";

const styles = `
  @keyframes wiggle {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-3px); }
    50% { transform: translateX(3px); }
    75% { transform: translateX(-3px); }
  }
  .wiggle {
    animation: wiggle 0.2s ease-in-out 3;
  }
  .disabled-overlay::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 8%;
    right: 8%;
    height: 1px;
    background-color: #9ca3af; /* gray-400 */
    transform: translateY(-50%);
    pointer-events: none;
  }
`;

const Recommended1 = ({ data }) => {
  const [quantities, setQuantities] = useState({});
  const [showQuantityControls, setShowQuantityControls] = useState({});

  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [themeColor, setThemeColor] = useState("#fffff");
  const swiperRef_1 = useRef(null);

  const [selectedSizes, setSelectedSizes] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProductIndex, setSelectedProductIndex] = useState(null);
  const navigate = useNavigate();
  const [message, setMessage] = useState(null);
  const [isQuantityLeftFactual, setIsQuantityLeftFactual] = useState(false);
  const [quantityLeftFactualValue, setQuantityLeftFactualValue] = useState(1);
  const [showStock, setShowStock] = useState(false);
  const [randomStocks, setRandomStocks] = useState([]);
  const [isMarketingKeysLoading, setIsMarketingKeysLoading] = useState(true);
  const admin_auto_id = sessionStorage.getItem("admin_auto_id");
  const app_type_id = sessionStorage.getItem("app_type_id");


  function lightenHexColor(hex, percent) {
    const num = parseInt(hex.replace("#", ""), 16);
    let r = (num >> 16) + 255 * percent;
    let g = ((num >> 8) & 0x00ff) + 255 * percent;
    let b = (num & 0x0000ff) + 255 * percent;
    r = Math.max(0, Math.min(255, Math.round(r)));
    g = Math.max(0, Math.min(255, Math.round(g)));
    b = Math.max(0, Math.min(255, Math.round(b)));
    return `rgb(${r}, ${g}, ${b})`;
  }

  function darkenHexColor(hex, percent) {
    const num = parseInt(hex.replace("#", ""), 16);
    let r = (num >> 16) - 255 * percent;
    let g = ((num >> 8) & 0x00ff) - 255 * percent;
    let b = (num & 0x0000ff) - 255 * percent;
    r = Math.max(0, Math.min(255, Math.round(r)));
    g = Math.max(0, Math.min(255, Math.round(g)));
    b = Math.max(0, Math.min(255, Math.round(b)));
    return `rgb(${r}, ${g}, ${b})`;
  }


  const clearMessage = () => {
    setTimeout(() => {
      setMessage(null);
    }, 2000); // Clear message after 2 seconds
  };

  const handleQuantityChange = (index, change) => {
    setQuantities((prev) => {
      const newQuantity = Math.max(0, (prev[index] || 0) + change);
      if (newQuantity === 0) {
        // Hide quantity controls if quantity is 0, show Plus button again
        setShowQuantityControls((prevControls) => ({
          ...prevControls,
          [index]: false,
        }));
      }
      return { ...prev, [index]: newQuantity };
    });
  };

  const showQuantity = (index) => {
    setShowQuantityControls((prev) => ({
      ...prev,
      [index]: true,
    }));
    setQuantities((prev) => ({
      ...prev,
      [index]: 1, // Set quantity to 1 when first clicked
    }));
  };

  // Fetch marketing keys
  useEffect(() => {
    const fetchMarketingKeys = async () => {
      setIsMarketingKeysLoading(true);
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/get_marketing_keys`,
          {
            admin_auto_id,
            app_type_id,
          }
        );

        if (response.data.status === "1" && response.data.data.length > 0) {
          const apiData = response.data.data[0];
          setIsQuantityLeftFactual(apiData.is_quantity_left_factual);
          setQuantityLeftFactualValue(apiData.quantity_left_factual_value);
        } else {
          console.error("Failed to fetch marketing keys:", response.data.msg);
        }
      } catch (error) {
        console.error("Error fetching marketing keys:", error);
      } finally {
        setIsMarketingKeysLoading(false);
      }
    };

    fetchMarketingKeys();
  }, [admin_auto_id, app_type_id]);

  // Set random stocks based on marketing keys
  useEffect(() => {
    if (isMarketingKeysLoading || selectedProductIndex === null) {
      return;
    }

    const selectedProduct = products[selectedProductIndex];
    if (!selectedProduct || !selectedProduct.sizes) return;

    const shouldShowStock = Math.random() < 0.9;
    setShowStock(shouldShowStock);

    if (shouldShowStock && selectedProduct.sizes.length > 0) {
      const stock = parseInt(quantityLeftFactualValue, 10);
      if (isNaN(stock) || stock <= 0) {
        setRandomStocks(selectedProduct.sizes.map(() => 0));
      } else {
        const newRandomStocks = selectedProduct.sizes.map(() =>
          Math.floor(Math.random() * stock) + 1
        );
        setRandomStocks(newRandomStocks);
      }
    } else {
      const originalStocks = selectedProduct.sizes.map((size) =>
        parseInt(size.size_stock, 10)
      );
      setRandomStocks(originalStocks);
    }
  }, [isMarketingKeysLoading, selectedProductIndex, quantityLeftFactualValue]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const adminId = sessionStorage.getItem("admin_auto_id");
        const appTypeId = sessionStorage.getItem("app_type_id");
        const accessToken = sessionStorage.getItem("accessToken");
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/get_new_product_list`,
          {
            admin_auto_id: adminId,
            app_type_id: appTypeId,
            component_auto_id: data._id,
            component_type: "Recommended",
            limit: 6,
            page_number: 1,
            customer_auto_id: accessToken || adminId,
          }
        );

        if (
          response.data.status === 1 &&
          Array.isArray(response.data.get_products_lists) &&
          response.data.get_products_lists.length > 0
        ) {
          const formattedProducts = response.data.get_products_lists.map(
            (product) => {
              const sizes =
                product.size && product.get_price_lists
                  ? product.size.map((s, i) => ({
                    size_name: s.size_name,
                    size_auto_id: s.size_auto_id,
                    size_price:
                      product.get_price_lists?.[i]?.size_price || "",
                    final_size_price:
                      product.get_price_lists?.[i]?.final_size_price || "",
                    size_stock: s.size_stock || "0"
                  }))
                  : [];

              return {
                id: product.product_auto_id,
                name: product.product_name,
                description: product.description,
                price: `₹${product.final_product_price}`,
                originalPrice: product.product_price
                  ? `₹${product.product_price}`
                  : undefined,
                discount: product.offer_percentage
                  ? `${product.offer_percentage}% off`
                  : undefined,
                delivery:
                  product.time && product.time_unit
                    ? `Delivery in ${product.time} ${product.time_unit}`
                    : "Delivery time unavailable",
                images: Array.isArray(product.product_images)
                  ? product.product_images.map(
                    (img) =>
                      `https://grobizapp-n.s3.ap-south-1.amazonaws.com/products_images/${img.product_image}`
                  )
                  : [],
                user_auto_id: product.user_auto_id,
                sizes, // merged sizes
                moq: product.moq,
              };
            }
          );
          setProducts(formattedProducts);
        } else {
          throw new Error("Failed to fetch products");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const handleSizeSelect = (productIndex, selectedSize) => {
    setSelectedSizes((prev) => ({
      ...prev,
      [productIndex]: selectedSize,
    }));
  };


  useEffect(() => {
    const storedStyle = sessionStorage.getItem("UIStyle");
    if (storedStyle) {
      const parsedStyle = JSON.parse(storedStyle);
      setThemeColor(parsedStyle.web_theme_colour);
    }
  }, []);

  if (loading || products.length === 0) {
    return null;
  }

  const openModalWithProduct = (index) => {
    setSelectedProductIndex(index);
    setModalOpen(true);
  };

  return (
    <div
      className={`p-4  relative `}
      style={{
        backgroundColor: data.web_background_color,
      }}
    >
      <style>{styles}</style>
      <div className="mb-4">
        <h1
          className={`text-2xl font-bold ${data.web_title_font} text-${data.title_alignment}`}
          style={{
            color: data.web_title_color,
          }}
        >
          {data.title}
        </h1>
        {/* <p
          className={`${data.web_title_font} text-${data.title_alignment}`}
          style={{
            color: data.web_title_color,
          }}
        >
          Most popular products near you!
        </p> */}
      </div>

      <Swiper
        ref={swiperRef_1}
        modules={[Navigation]}
        spaceBetween={20}
        slidesPerView={1}
        navigation={{
          nextEl: `.custom-next-${data._id}`,
          prevEl: `.custom-prev-${data._id}`,
        }}
        breakpoints={{
          0: {
            slidesPerView: 2.3,
          },
          640: {
            slidesPerView: 2,
          },
          768: {
            slidesPerView: 3,
          },
          1024: {
            slidesPerView: 4.5,
          },
        }}
        onSlideChange={(swiper) => {
          setIsBeginning(swiper.isBeginning);
          setIsEnd(swiper.isEnd);
        }}
        onInit={(swiper) => {
          setIsBeginning(swiper.isBeginning);
          setIsEnd(swiper.isEnd);
        }}
      >
        {products.map((product, index) => (
          <SwiperSlide key={index}>
            <Link
              to={`/product-detail/${product.id}`}
              className="no-underline text-inherit"
            >
              <div className="relative h-full p-1 bg-white border rounded-xl">
                {/* Container for Plus button and quantity control */}
                <div className="relative mb-2">
                  {/* Plus Button (Hidden when quantity is shown) */}
                  {!showQuantityControls[index] && (
                    <button
                      className="absolute p-2 rounded-lg top-40 right-2 hover:brightness-105"
                      style={{
                        backgroundColor: themeColor,
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        openModalWithProduct(index);
                      }}
                    >
                      <FaPlus className="text-gray-700" />
                    </button>
                  )}

                  {/* Quantity Controls (Show when Plus button is clicked) */}
                  {/* {showQuantityControls[index] && (
                  <div
                    className="absolute flex items-center gap-2 mb-2 rounded-lg top-40 right-2"
                    style={{
                      backgroundColor: themeColor,
                    }}
                  >
                    <button
                      onClick={() => handleQuantityChange(index, -1)}
                      className="p-2 rounded-lg hover:brightness-105 disabled:opacity-50"
                      disabled={!quantities[index]}
                      style={{
                        backgroundColor: themeColor,
                      }}
                    >
                      <FaMinus className="w-4 h-4" />
                    </button>
                    <span className="w-8 text-center">
                      {quantities[index] || 0}
                    </span>
                    <button
                      onClick={() => handleQuantityChange(index, 1)}
                      className="p-2 bg-gray-100 rounded-lg hover:brightness-105"
                      style={{
                        backgroundColor: themeColor,
                      }}
                    >
                      <FaPlus className="w-4 h-4" />
                    </button>
                  </div>
                )} */}
                </div>

                {/* Image Section */}
                <div className="h-48 mb-4 overflow-hidden rounded-xl">
                  <img
                    src={
                      product.images && product.images.length > 0
                        ? product.images[0]
                        : "https://placehold.co/600x400"
                    }
                    alt={product.name}
                    className="object-cover w-full h-full"
                  />
                </div>

                {/* Product Details */}
                <h3 className="mb-2 text-lg font-semibold truncate">
                  {product.name}
                </h3>

                <p className="text-gray-600 text-sm mb-2 min-h-[20px]">
                  {product.description ? product.description : " "}
                </p>

                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl font-bold">{product.price}</span>
                  {product.discount &&
                    parseFloat(product.discount.replace("% off", "")) > 0 && (
                      <>
                        <span className="text-sm text-gray-500 line-through">
                          {product.originalPrice}
                        </span>
                        <span className="text-sm text-red-600">
                          {product.discount}
                        </span>
                      </>
                    )}
                </div>

                <p
                  className="flex items-center text-sm font-medium"
                  style={{ color: themeColor }}
                >
                  <MdElectricBolt className="mr-1 text-yellow-600" />{" "}
                  {product.delivery}
                </p>
              </div>
            </Link>
          </SwiperSlide>
        ))}

        {/* Navigation Arrows */}
        <div
          className={`custom-prev-${data._id
            } absolute top-1/2 left-2 transform -translate-y-1/2 z-10 cursor-pointer bg-white p-2 rounded-full shadow-lg ${isBeginning ? "opacity-0 pointer-events-none" : "opacity-100"
            }`}
        >
          <FaArrowLeft className="text-xl text-gray-700" />
        </div>
        <div
          className={`custom-next-${data._id
            } absolute top-1/2 right-2 transform -translate-y-1/2 z-10 cursor-pointer bg-white p-2 rounded-full shadow-lg ${isEnd ? "opacity-0 pointer-events-none" : "opacity-100"
            }`}
        >
          <FaArrowRight className="text-xl text-gray-700" />
        </div>
      </Swiper>
      {modalOpen && selectedProductIndex !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="relative w-full max-w-md p-4 bg-white rounded-lg">
            <button
              className="absolute text-gray-500 top-2 right-2 hover:text-black"
              onClick={() => setModalOpen(false)}
            >
              ✕
            </button>

            {/* Modal Product Data */}
            <div className="mb-4">
              <Swiper
                spaceBetween={10}
                slidesPerView={2.5}
                className="w-full h-60"
              >
                {products[selectedProductIndex].images.map((image, imgIndex) => (
                  <SwiperSlide key={imgIndex}>
                    <img
                      src={image}
                      alt={`${products[selectedProductIndex].name}-${imgIndex}`}
                      className="object-cover w-full rounded-md h-60"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>

              <h2 className="mb-1 text-xl font-bold">
                {products[selectedProductIndex].name}
              </h2>
              <p className="mb-2 text-gray-600">
                {products[selectedProductIndex].description}
              </p>

              {/* Size Selector */}
              <div className="flex flex-wrap gap-2">
                {products[selectedProductIndex].sizes.map((size, sizeIndex) => {
                  const actualStock = parseInt(size.size_stock, 10);
                  const isDisabled =
                    !actualStock || isNaN(actualStock) || actualStock <= 0;
                  const isSelected =
                    selectedSizes[selectedProductIndex]?.size_name === size.size_name;
                  const displayStock = showStock
                    ? randomStocks[sizeIndex]
                    : actualStock;
                  const showStockLabel =
                    actualStock > 0 &&
                    showStock &&
                    isQuantityLeftFactual &&
                    displayStock <= 5;

                  return (
                    <div key={size.size_auto_id} className="relative">
                      <button
                        className={`relative border w-20 h-12 rounded-full text-sm flex items-center justify-center flex-col transition-colors duration-200 overflow-hidden
                              ${isDisabled ? "bg-gray-200 text-gray-400 border-gray-300 cursor-not-allowed disabled-overlay" : ""}
                              ${!isDisabled && isSelected ? "text-white" : ""}`}
                        style={{
                          backgroundColor:
                            !isDisabled && isSelected ? themeColor : undefined,
                          borderColor:
                            !isDisabled && isSelected ? themeColor : undefined,
                          color: !isDisabled && isSelected ? "#ffffff" : undefined,
                        }}
                        onClick={() => !isDisabled && handleSizeSelect(selectedProductIndex, size)}
                        onMouseEnter={(e) => {
                          if (!isSelected && !isDisabled) {
                            e.currentTarget.style.backgroundColor = themeColor;
                            e.currentTarget.style.color = "#ffffff";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected && !isDisabled) {
                            e.currentTarget.style.backgroundColor = "#ffffff";
                            e.currentTarget.style.color = "#000000";
                          }
                        }}
                        disabled={isDisabled}
                      >
                        <span
                          className={`text-base leading-none ${showStockLabel ? "mb-3" : ""
                            }`}
                        >
                          {size.size_name}
                        </span>
                        {showStockLabel && (
                          <span
                            className="absolute bottom-0 left-0 right-0 px-2 py-[4px] text-[9px] text-center text-white leading-tight"
                            style={{ backgroundColor: themeColor }}
                          >
                            {displayStock} left
                          </span>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Dynamic Price Display */}
              <div className="mb-3">
                <p className="text-lg font-bold">
                  {selectedSizes[selectedProductIndex]?.final_size_price
                    ? `₹${selectedSizes[selectedProductIndex]?.final_size_price}`
                    : products[selectedProductIndex].price}
                </p>
              </div>

              {/* Quantity Control */}
              <div className="flex items-center gap-2 mb-3">
                <button
                  onClick={() => handleQuantityChange(selectedProductIndex, -1)}
                  className="p-2 bg-gray-200 rounded-lg"
                  disabled={!quantities[selectedProductIndex]}
                >
                  <FaMinus />
                </button>
                <input
                  type="number"
                  min="1"
                  value={quantities[selectedProductIndex] || 1}
                  onChange={(e) => {
                    const value = Math.max(1, parseInt(e.target.value));
                    setQuantities((prev) => ({
                      ...prev,
                      [selectedProductIndex]: value,
                    }));
                  }}
                  className="w-12 text-center border border-gray-300 rounded py-1 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none appearance-none"
                />

                <button
                  onClick={() => handleQuantityChange(selectedProductIndex, 1)}
                  className="p-2 bg-gray-200 rounded-lg"
                >
                  <FaPlus />
                </button>
              </div>


              {/* Display Message */}
              {message && (
                <div className="mt-3 text-sm text-center" style={{ color: message.includes("successfully") ? "green" : "red" }}>
                  {message}
                </div>
              )}

              {/* Add to Cart or Continue Button */}
              <button
                onClick={async () => {
                  setMessage(null);
                  const selectedProduct = products[selectedProductIndex];
                  const selectedSize = selectedSizes[selectedProductIndex];
                  const quantity = quantities[selectedProductIndex] || 1;
                  const moq = selectedProduct.moq || "1";
                  const parsedQuantity = parseInt(quantity, 10);
                  const parsedMoq = parseInt(moq, 10);
                  const actualStock = selectedSize ? parseInt(selectedSize.size_stock, 10) : null;

                  if (parsedQuantity < parsedMoq) {
                    setMessage(`Minimum order quantity is ${parsedMoq}`);
                    clearMessage();
                    return;
                  }

                  if (selectedProduct.sizes.length > 0 && !selectedSize) {
                    setMessage("Please select a size");
                    clearMessage();
                    return;
                  }

                  if (selectedProduct.sizes.length > 0 && actualStock === 0) {
                    setMessage("Selected size is out of stock");
                    clearMessage();
                    return;
                  }

                  const productData = {
                    product_auto_id: selectedProduct.id,
                    added_by_id: selectedProduct.user_auto_id,
                    cart_quantity: parsedQuantity,
                  };

                  if (selectedSize) {
                    const selectedSizePrice =
                      selectedSize.final_size_price || selectedSize.size_price;
                    productData.size = selectedSize.size_auto_id;
                    productData.size_price = selectedSizePrice;
                  }

                  try {
                    const res = await addToCart(productData);
                    await getCartCount();
                    if (res?.status === "1") {
                      setMessage(res.message);
                      clearMessage();
                      setTimeout(() => {
                        navigate("/cart");
                        setModalOpen(false);
                      }, 1000);
                    } else {
                      setMessage(res.message);
                      clearMessage();
                    }
                  } catch (error) {
                    setMessage(error.message);
                    clearMessage();
                    console.error("Failed to add to cart:", error);
                  }
                }}
                className={`w-full py-2 text-white rounded-lg ${selectedSizes[selectedProductIndex]?.size_stock === "0" ||
                  (products[selectedProductIndex].sizes.length > 0 && !selectedSizes[selectedProductIndex])
                  ? "bg-gray-400 cursor-not-allowed"
                  : "cursor-pointer"
                  }`}
                style={{
                  backgroundColor:
                    selectedSizes[selectedProductIndex]?.size_stock === "0" ||
                      (products[selectedProductIndex].sizes.length > 0 && !selectedSizes[selectedProductIndex])
                      ? "#ccc"
                      : themeColor,
                }}
                onMouseEnter={(e) => {
                  if (
                    !(
                      selectedSizes[selectedProductIndex]?.size_stock === "0" ||
                      (products[selectedProductIndex].sizes.length > 0 && !selectedSizes[selectedProductIndex])
                    )
                  ) {
                    const darkenedColor = darkenHexColor(themeColor, 0.15);
                    e.currentTarget.style.backgroundColor = darkenedColor;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    selectedSizes[selectedProductIndex]?.size_stock === "0" ||
                      (products[selectedProductIndex].sizes.length > 0 && !selectedSizes[selectedProductIndex])
                      ? "#ccc"
                      : themeColor;
                }}
              >
                {products[selectedProductIndex].sizes.length > 0 && !selectedSizes[selectedProductIndex]
                  ? "SELECT A SIZE"
                  : selectedSizes[selectedProductIndex]?.size_stock === "0"
                    ? "Size Out of Stock"
                    : `Add to Cart | ₹ ${selectedSizes[selectedProductIndex]?.final_size_price ||
                    products[selectedProductIndex].price.replace("₹", "")
                    }`}
              </button>


            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Recommended1;
